import {useState, useRef, useEffect, useMemo} from 'react';
import {useSelector, shallowEqual} from 'react-redux';
import cx from 'classnames';
import {ORIGIN_DICT} from '~/common/constants/event-origins';
import {PremiumDesign} from './PremiumDesign';
import {PremiumInsert} from '~/ViewInvitation/routes/Landing/Premium/PremiumInsert';
import {ChevronIcon} from '~library/svg/ChevronIcon';
import {TriangleIcon} from '~library/svg/TriangleIcon';
import {webviewAnimFinish, webviewAnimReplay, webviewAnimStart} from './webviewMessages';
import {selectVideoId} from '~/CreateFabricInvitation/reducers/eventDetails/selectors';
import {OverlayAnimation} from '~/common/OverlayAnimation/OverlayAnimation';
import {selectEventOptionsDesign} from '~/CreateFabricInvitation/reducers/eviteEvent/selectors';
import {useVideoControl} from '~/common/hooks/useVideoControls';

export const CarouselDots = (props) => {
  const {numPages, setPage, currentPage} = props;
  const pages = Array.from(Array(Math.min(numPages, 16)).keys());
  return (
    <span className="premium-carousel__dots">
      {pages.map((p) => (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <span
          className={p === currentPage ? 'premium-carousel__dot__active' : 'premium-carousel__dot'}
          onClick={() => setPage(p)}
          key={`premium-carousel-dot-${p}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setPage(p);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`${p + 1} of ${numPages} page link`}
        />
      ))}
    </span>
  );
};

export const PremiumCarousel = ({
  designTemplateRef,
  fabricArt,
  height,
  isWebview,
  preview,
  isViewInvitationPage,
  onCanPlayAnimation,
}) => {
  const invitationRootEl =
    document.querySelector('#inviteRoot') ||
    document.querySelector('.create-flow__invitation-preview');
  const invitationRootTop = invitationRootEl ? invitationRootEl.offsetTop : 50;

  const [page, setPage] = useState(0);
  const [overlayTop, setOverlayTop] = useState(invitationRootTop);
  const revealFinishedRef = useRef(false);
  const firstRender = useRef(true);
  const alreadyRanAnimation = useRef(false);
  const containerRef = useRef();
  const invitation = useSelector((s) => s.premiumDesign?.invitation || {}, shallowEqual);
  const inserts = useSelector((s) => s.premiumDesign?.inserts || [], shallowEqual);
  const origin = useSelector((s) => s.eventDetails.origin);
  const videoId = useSelector(selectVideoId);
  const eventOptionsDesign = useSelector(selectEventOptionsDesign, shallowEqual);
  const {reveal_id, overlay_id} = eventOptionsDesign || {};
  const effectVideo = useVideoControl();
  const revealVideo = useVideoControl();
  const fabricDesign = useSelector((s) => s.fabricDesign, shallowEqual);

  const [display, setDisplay] = useState(''); // EVT-32862: hides overlays to address iOS bug with pledge donor feed modal

  const {play: playEffect = () => {}, hide: hideEffect = () => {}} = effectVideo;
  const {play: playReveal = () => {}} = revealVideo;

  const hasEffect = !!(overlay_id || fabricDesign?.overlayId);
  const hasReveal = !!(reveal_id || fabricDesign?.revealId);

  const numPages = inserts.length + 1 + (videoId ? 1 : 0);

  const doSetPage = (p) => {
    if (p < 0 || p >= numPages) return;
    containerRef.current.scrollLeft = `${document.body.clientWidth * p}`;
    setPage(p);
  };

  const paginateRight = () => {
    if (page >= numPages - 1) return;
    doSetPage(page + 1);
  };

  const paginateLeft = () => {
    if (page < 1) return;
    doSetPage(page - 1);
  };

  const handleScroll = () => {
    const position = containerRef.current.scrollLeft;
    const newPage = Math.floor(0.5 + position / document.body.clientWidth);
    if (newPage !== page) setPage(newPage);
  };

  const yourInvitationRef = useRef();
  const yourInvitationSizeRef = useRef();
  const inviteContainerRef = useRef();
  const desktopInviteRef = useRef();
  const replayButtonRef = useRef();
  const fabricCardRef = useRef();
  const stickerContainerRef = useRef();
  const envelopeWidthRef = useRef();

  const animationFinished = () => {
    replayButtonRef?.current?.classList.remove('hidden');

    if (preview) {
      const editArtworkBtn = document.getElementById('editArtworkBtn');
      editArtworkBtn?.classList.remove('hidden');
    }
    alreadyRanAnimation.current = true;
    webviewAnimFinish();
  };

  const waitForReveal = () =>
    new Promise((resolve) => {
      const interval = setInterval(() => {
        if (revealFinishedRef.current) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });

  const envelopeAnimation = () => {
    /** If a reveal is selected, play it before the envelope animation */

    setTimeout(async () => {
      inviteContainerRef?.current?.classList.remove('invite-container');
      inviteContainerRef?.current?.classList.add('invite-container');

      webviewAnimStart();
      if (hasReveal) {
        await waitForReveal();
      }
      inviteContainerRef?.current?.classList.remove('step-0');
      inviteContainerRef?.current?.classList.add('step-1');

      setTimeout(() => {
        stickerContainerRef?.current?.classList.add('flipped');
      }, 300);

      setTimeout(() => {
        inviteContainerRef?.current?.classList.remove('step-1');
        inviteContainerRef?.current?.classList.add('step-2');

        setTimeout(() => {
          inviteContainerRef?.current?.classList.remove('step-2');
          inviteContainerRef?.current?.classList.add('step-3');

          setTimeout(() => {
            inviteContainerRef?.current?.classList.remove('step-3');
            inviteContainerRef?.current?.classList.add('step-4');

            setTimeout(() => {
              inviteContainerRef?.current?.classList.remove('step-4');
              inviteContainerRef?.current?.classList.add('step-5');
              playEffect();
              setTimeout(() => {
                animationFinished();
              }, 1000);
            }, 500);
          }, 250);
        }, 500);
      }, 1000);
    }, 300);
  };

  const replayAnimation = () => {
    setDisplay('');
    playReveal();
    replayButtonRef?.current?.classList.add('hidden');
    setTimeout(() => {
      revealFinishedRef.current = false;
      stickerContainerRef?.current?.classList.remove('flipped');
      if (preview) {
        const editArtworkBtn = document.getElementById('editArtworkBtn');
        editArtworkBtn?.classList.add('hidden');
      }

      inviteContainerRef?.current?.classList.add('reset');
      webviewAnimReplay();

      setTimeout(() => {
        inviteContainerRef?.current?.classList.remove('reset');
        inviteContainerRef?.current?.classList.add('step-0');
        envelopeAnimation();
      }, 1000);
    }, 500);
  };

  useEffect(() => {
    if (firstRender.current && !alreadyRanAnimation.current && invitation) {
      setTimeout(() => {
        envelopeAnimation();
      }, 2000);
    }
  }, [firstRender.current, alreadyRanAnimation.current, !!invitation]);

  useEffect(() => {
    if (!firstRender.current && alreadyRanAnimation.current) {
      const editArtworkBtn = document.getElementById('editArtworkBtn');
      if (!preview) {
        editArtworkBtn?.classList.add('hidden');
      }
    }
  }, [firstRender.current, alreadyRanAnimation.current, preview]);

  useEffect(() => {
    if (!alreadyRanAnimation.current) {
      hideEffect();
      return;
    }
    if (preview) {
      replayAnimation();
    }
  }, [preview, alreadyRanAnimation.current]);

  useEffect(() => {
    if (!hasReveal) {
      onCanPlayAnimation();
    }
    firstRender.current = false;
    hideEffect();
    // if app webview, set replayAnimation function to be called by the app
    if (isWebview) {
      window.replayAnimation = replayAnimation;
    }

    // Position: sticky won't work here so we're simulating the effect
    const forceOverlayTop = () => {
      if (window.scrollY < invitationRootTop) {
        setOverlayTop(`${invitationRootTop - window.scrollY}px`);
      } else {
        setOverlayTop('0px');
      }
    };
    window.addEventListener('scroll', forceOverlayTop);
    return () => window.removeEventListener('scroll', forceOverlayTop);
  }, []);

  const overlayClass = isViewInvitationPage
    ? 'overlay-animation--view-invitation-overlay'
    : 'overlay-animation--preview-invitation-overlay';

  const effectAnimation = useMemo(
    () => (
      <div
        className={overlayClass}
        style={{top: isViewInvitationPage ? overlayTop : null, display}}
      >
        <OverlayAnimation
          eventOptionsDesign={eventOptionsDesign}
          effectVideo={effectVideo}
          hideOnLoad
          onEnded={() => setDisplay('none')}
        />
      </div>
    ),
    [eventOptionsDesign, effectVideo, overlayTop, isViewInvitationPage, display]
  );

  const revealAnimation = useMemo(
    () => (
      <div
        className={overlayClass}
        style={{top: isViewInvitationPage ? overlayTop : null, display}}
      >
        <OverlayAnimation
          eventOptionsDesign={eventOptionsDesign}
          revealVideo={revealVideo}
          onCanPlay={onCanPlayAnimation}
          onEnded={() => {
            revealFinishedRef.current = true;
          }}
          autoPlay
          portalToBody={isViewInvitationPage}
        />
      </div>
    ),
    [eventOptionsDesign, revealVideo, overlayTop, isViewInvitationPage, display]
  );

  // TODO: Split carousel logic to a reusable component
  return (
    <>
      {hasEffect && effectAnimation}
      {hasReveal && revealAnimation}
      <div className="premium-carousel__container">
        {numPages > 1 && (
          <button
            className="unstyled-button paginate-left"
            onClick={paginateLeft}
            disabled={page < 1}
            type="button"
            aria-label="Previous page"
          >
            <ChevronIcon direction="left" />
          </button>
        )}
        <div className="premium-carousel" onScroll={handleScroll} ref={containerRef}>
          <div
            className={cx('premium-carousel__page', {
              fabric: origin === ORIGIN_DICT.FABRIC,
            })}
          >
            <PremiumDesign
              isWebview={isWebview}
              height={height}
              invitation={invitation}
              designTemplateRef={designTemplateRef}
              yourInvitationRef={yourInvitationRef}
              yourInvitationSizeRef={yourInvitationSizeRef}
              inviteContainerRef={inviteContainerRef}
              desktopInviteRef={desktopInviteRef}
              envelopeWidthRef={envelopeWidthRef}
              fabricCardRef={fabricCardRef}
              stickerContainerRef={stickerContainerRef}
              preview={preview}
              fabricArt={fabricArt}
            />
          </div>
          {inserts.map((insert, index) => (
            <div
              className="premium-carousel__page premium-carousel__page__insert"
              key={`premium-carousel-insert-${index + 1}`}
            >
              <PremiumInsert insert={insert} />
            </div>
          ))}
        </div>
        {numPages > 1 && (
          <button
            className="unstyled-button paginate-right"
            onClick={paginateRight}
            disabled={page >= numPages - 1}
            type="button"
            aria-label="Next page"
          >
            <ChevronIcon direction="right" />
          </button>
        )}
        {numPages > 1 && (
          <CarouselDots numPages={numPages} currentPage={page} setPage={doSetPage} />
        )}
        {page === 0 && !isWebview && (
          <button
            className="animation-replay-button hidden"
            ref={replayButtonRef}
            onClick={() => replayAnimation()}
            data-qa-id="replay-animation"
            type="button"
          >
            <TriangleIcon ratio={1.1} />
            Replay
          </button>
        )}
      </div>
    </>
  );
};
